<template>
  <v-card flat>
    <v-card-title>Endereço</v-card-title>
    <v-divider />
    <v-card-text>
      <v-form
        ref="formRef"
        v-model="formValid"
        @update:model-value="emitValidity"
      >
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              label="CEP"
              v-model="form.cep"
              v-maska="'#####-###'"
              :rules="[validationRules.required]"
              @blur="onCepChanged"
              :disabled="disabled || loading"
              :loading="loading"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.street"
              :rules="[validationRules.required]"
              label="Logradouro"
              :disabled="disabled || loading"
              :loading="loading"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.neighborhood"
              :rules="[validationRules.required]"
              label="Bairro"
              :disabled="disabled || loading"
              :loading="loading"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              label="Complemento (opcional)"
              v-model="form.complement"
              :disabled="disabled || loading"
              :loading="loading"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.number"
              :rules="[validationRules.required, validationRules.maxLength(10)]"
              label="Número"
              :disabled="disabled || loading"
              :loading="loading"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.city"
              :rules="[validationRules.required]"
              label="Cidade"
              :disabled="disabled || loading"
              :loading="loading"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              :items="BRAZILIAN_STATES"
              item-title="nome"
              item-value="uf"
              :rules="[validationRules.required]"
              v-model="form.state"
              label="Estado"
              :disabled="disabled || loading"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.codigo_municipio"
              :rules="[
                validationRules.minLength(1),
                validationRules.maxLength(7),
              ]"
              label="Código do município"
              :disabled="disabled || loading"
              :loading="loading"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, watch } from "vue";
import { useCEPQuery } from "@/queries/modules/external";
import { validationRules } from "@/utils/validation";
import { useSnackbar } from "@/plugins/snackbar";

const BRAZILIAN_STATES = [
  { uf: "AC", nome: "Acre" },
  { uf: "AL", nome: "Alagoas" },
  { uf: "AP", nome: "Amapá" },
  { uf: "AM", nome: "Amazonas" },
  { uf: "BA", nome: "Bahia" },
  { uf: "CE", nome: "Ceará" },
  { uf: "DF", nome: "Distrito Federal" },
  { uf: "ES", nome: "Espírito Santo" },
  { uf: "GO", nome: "Goiás" },
  { uf: "MA", nome: "Maranhão" },
  { uf: "MT", nome: "Mato Grosso" },
  { uf: "MS", nome: "Mato Grosso do Sul" },
  { uf: "MG", nome: "Minas Gerais" },
  { uf: "PA", nome: "Pará" },
  { uf: "PB", nome: "Paraíba" },
  { uf: "PR", nome: "Paraná" },
  { uf: "PE", nome: "Pernambuco" },
  { uf: "PI", nome: "Piauí" },
  { uf: "RJ", nome: "Rio de Janeiro" },
  { uf: "RN", nome: "Rio Grande do Norte" },
  { uf: "RS", nome: "Rio Grande do Sul" },
  { uf: "RO", nome: "Rondônia" },
  { uf: "RR", nome: "Roraima" },
  { uf: "SC", nome: "Santa Catarina" },
  { uf: "SP", nome: "São Paulo" },
  { uf: "SE", nome: "Sergipe" },
  { uf: "TO", nome: "Tocantins" },
];

const props = defineProps({
  form: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(["errors"]);

const $snackbar = useSnackbar();

const formRef = ref(null);
const formValid = ref(false);
const cepToLookup = ref(null);

// TanStack Query for CEP lookup
const cepQuery = useCEPQuery(cepToLookup);
const loading = ref(false);

// Watch for CEP query results
watch(
  () => cepQuery.data.value,
  (data) => {
    if (data) {
      props.form.street = data.logradouro;
      props.form.neighborhood = data.bairro;
      props.form.city = data.localidade;
      props.form.complement = data.complemento;
      props.form.state = data.uf;
      props.form.codigo_municipio = data.ibge;
      loading.value = false;
    }
  },
);

watch(
  () => cepQuery.isError.value,
  (isError) => {
    if (isError) {
      $snackbar.warning("Ocorreu um erro ao buscar as informações do CEP");
      loading.value = false;
    }
  },
);

const emitValidity = (valid) => {
  emit("errors", !valid);
};

const validate = async () => {
  const { valid } = await formRef.value.validate();
  emit("errors", !valid);
  return valid;
};

defineExpose({ validate });

const onCepChanged = () => {
  const cep = props.form.cep;
  if (cep && cep.length === 9 && cep !== cepToLookup.value) {
    loading.value = true;
    cepToLookup.value = cep;
  }
};
</script>
